<template>
  <Teleport to="[data-header-action]" v-if="isMounted">
    <div class="[ flex gap-3 ]">
      <ButtonComponent label="Create a task" outlined @click="createTask">
        <template #icon>
          <JBIcon name="add" />
        </template>
      </ButtonComponent>
    </div>
  </Teleport>
  <div v-bind="$attrs" class="[ flex flex-col gap-4 max-w-5xl mx-auto ]">
    <div class="[ flex justify-between ]">
      <Tabs v-model:value="selectedStatus">
        <TabList>
          <Tab :value="TaskStatusType.Open">Ongoing</Tab>
          <Tab :value="TaskStatusType.Closed">Completed</Tab>
        </TabList>
      </Tabs>
      <div class="[ flex gap-3 ]" v-if="isActionEnabled">
        <ButtonComponent
          :label="'Delete' + actionSuffix"
          outlined
          :disabled="!isActionEnabled"
          @click="deleteTasks"
        />
        <ButtonComponent
          :label="statusChangeButtonLabel + actionSuffix"
          outlined
          :disabled="!isActionEnabled"
          @click="toggleStatus"
        />
      </div>
    </div>

    <DataTable
      :value="tasks"
      :loading="taskStore.isTaskListLoading"
      :show-headers="false"
      sort-field="dueDate"
      v-model:selection="selectedTasks"
    >
      <template #empty>
        <Empty
          icon="task-done"
          :title="emptyTitle"
          :message="emptyMessage"
        ></Empty>
      </template>
      <Column selectionMode="multiple" class="[ w-10 ]"></Column>
      <Column field="text" header="Text" sortable="true" class="[ w-1/2 ]">
        <template #body="{ data }: { data: Task }">
          <div
            class="[ cursor-pointer font-bold ]"
            @click="showTaskDetail(data)"
          >
            {{ data.title }}
          </div>
        </template>
      </Column>
      <Column field="dateCreated" sortable="true">
        <template #body="{ data }: { data: Task }">
          <span class="[ text-gray-dark ]">Created:</span>
          {{
            data.dateCreated
              ? formatRelative(data.dateCreated, new Date())
              : "-"
          }}
        </template>
      </Column>
      <Column column-key="links" class="[ w-4 ]">
        <template #body="{ data: _data }: { data: Task }">
          <ButtonComponent plain label="2" severity="secondary">
            <template #icon>
              <JBIcon name="link" />
            </template>
          </ButtonComponent>
        </template>
      </Column>
      <Column field="priority">
        <template #body="{ data }: { data: Task }">
          <PriorityRanking :score="parseNumberAsPriorityScore(data.priority)" />
        </template>
      </Column>
    </DataTable>
  </div>

  <CreateTaskModal
    v-model="showCreateTaskModal"
    :task="selectedTask || undefined"
    :edit-handler="taskStore.updateTask"
    :create-handler="taskStore.createTask"
    @task-created="taskStore.fetchTasks"
  />

  <TaskDetailPanel
    :task="selectedTask"
    v-model="showTaskDetailDrawer"
    @edit-task="editTask"
    @delete-task="deleteTask"
    @toggleStatus="toggleTaskStatus"
  />
</template>

<script setup lang="ts">
import CreateTaskModal from "@/components/contact/realtime/tasks/CreateTaskModal.vue";
import TaskDetailPanel from "@/components/tasks/TaskDetailPanel.vue";
import Empty from "@/components/ui/Empty.vue";
import { Task, TaskStatusType } from "@/services/api/sdk";
import { useTaskStore } from "@/store/task.ts";
import { useMounted } from "@vueuse/core";
import {
  Button as ButtonComponent,
  DataTable,
  Column,
  Tabs,
  Tab,
  TabList,
} from "primevue";
import { computed, onMounted, ref } from "vue";
import { formatRelative } from "date-fns";
import PriorityRanking from "@/components/ui/PriorityRanking.vue";
import { parseNumberAsPriorityScore } from "@/utils/task.ts";
import { storeToRefs } from "pinia";

definePage({
  meta: {
    icon: "task-done",
    title: "Tasks",
    scope: "Task.Read",
  },
});

defineOptions({
  inheritAttrs: false,
});

const taskStore = useTaskStore();

const showCreateTaskModal = ref(false);
const selectedStatus = ref<TaskStatusType>(TaskStatusType.Open);
const selectedTasks = ref<Task[]>([]);
const showTaskDetailDrawer = ref<boolean>(false);

const { selectedTask, taskList } = storeToRefs(taskStore);

const isMounted = useMounted();

const tasks = computed(() =>
  taskList.value?.filter((x) => x.status === selectedStatus.value),
);

const emptyTitle = computed(() =>
  selectedStatus.value === TaskStatusType.Open ? "All done" : "No closed tasks",
);
const emptyMessage = computed(() =>
  selectedStatus.value === TaskStatusType.Open
    ? "You completed all your tasks"
    : "There are no closed tasks",
);

const actionSuffix = computed(() => {
  if (selectedTasks.value.length === tasks.value.length) {
    return " all tasks";
  }
  return (
    ` ${selectedTasks.value.length} task` +
    (selectedTasks.value.length > 1 ? "s" : "")
  );
});
const statusChangeButtonLabel = computed(() =>
  selectedStatus.value === TaskStatusType.Open ? "Close" : "Reopen",
);
const isActionEnabled = computed(() => selectedTasks.value.length > 0);

onMounted(() => {
  taskStore.fetchTasks();
});

async function toggleStatus() {
  await Promise.all(selectedTasks.value.map(toggleTaskStatus));
  selectedTasks.value = [];
}

async function toggleTaskStatus(task: Task) {
  const action =
    selectedStatus.value === TaskStatusType.Open
      ? taskStore.closeTask
      : taskStore.openTask;

  await action(task.id!);
}

async function deleteTasks() {
  await Promise.all(selectedTasks.value.map((x) => deleteTask(x)));
  selectedTasks.value = [];
}

async function deleteTask(task: Task) {
  await taskStore.deleteTask(task.id!);
}

function editTask(task: Task) {
  taskStore.selectTask(task.id!);
  showCreateTaskModal.value = true;
}

function createTask() {
  showCreateTaskModal.value = true;
}

function showTaskDetail(task: Task) {
  showTaskDetailDrawer.value = true;
  taskStore.selectTask(task.id!);
}
</script>
